import { SOCKET_CHANNELS } from "../../constants/constants";

function pickAvatar(p) {
  return (
    p.avatar ||
    p.author_avatar ||
    p.sender_avatar ||
    p.user_avatar ||
    p.profile_avatar ||
    null
  );
}

function pickActorName(p) {
  const n =
    p.author ||
    p.sender ||
    p.author_name ||
    p.full_name ||
    p.username ||
    "";
  return n ? String(n) : "";
}

/** Wie de melding “visueel” vertegenwoordigt t.o.v. de ingelogde gebruiker (mail). */
function mailActorForViewer(p, viewerUserId) {
  const avatar = pickAvatar(p);
  const fallbackName = pickActorName(p);
  if (viewerUserId == null || viewerUserId === "") {
    return { actorName: fallbackName || null, avatarUrl: avatar || null };
  }
  const v = String(viewerUserId);
  if (p.receiver_id != null && String(p.receiver_id) === v) {
    return {
      actorName: p.sender ? String(p.sender) : fallbackName || null,
      avatarUrl: avatar || null,
    };
  }
  if (p.sender_id != null && String(p.sender_id) === v) {
    return {
      actorName: p.receiver ? String(p.receiver) : fallbackName || null,
      avatarUrl: avatar || null,
    };
  }
  return { actorName: fallbackName || null, avatarUrl: avatar || null };
}

/** Map socket event name → user-facing notification + optional deep link */
export function notificationFromSocketEvent(eventName, payload, viewerUserId) {
  const p = payload && typeof payload === "object" ? payload : {};

  switch (eventName) {
    case SOCKET_CHANNELS.MAIL: {
      const { actorName, avatarUrl } = mailActorForViewer(p, viewerUserId);
      return {
        title: "Berichten",
        message: p.subject ? String(p.subject) : "Nieuw bericht of update in uw postvak.",
        actorName,
        avatarUrl,
        type: "info",
        link: "/mailbox",
        source: SOCKET_CHANNELS.MAIL,
      };
    }
    case SOCKET_CHANNELS.PREINTAKE:
      if (p._forAdmins) {
        return {
          title: "Pre-intake",
          message:
            p.kind === "preintake_verstuurd"
              ? "Een cliënt heeft de pre-intake verstuurd."
              : "Er is een update bij de pre-intake van een cliënt.",
          type: "info",
          link: "/dashboard",
          source: SOCKET_CHANNELS.PREINTAKE,
        };
      }
      if (p.kind === "preintake_admin_note") {
        return {
          title: "Pre-intake",
          message: "Uw begeleider heeft een opmerking bij uw pre-intake gezet.",
          type: "info",
          link: "/healthforms",
          source: SOCKET_CHANNELS.PREINTAKE,
        };
      }
      return {
        title: "Pre-intake",
        message: "Er is een update op uw pre-intake / gezondheidsprofiel.",
        type: "info",
        link: "/healthforms",
        source: SOCKET_CHANNELS.PREINTAKE,
      };
    case SOCKET_CHANNELS.EETDAGBOEK:
      if (p._forAdmins) {
        return {
          title: "Eetdagboek",
          message: "Een cliënt heeft het eetdagboek ingevuld of gewijzigd.",
          type: "info",
          link: "/dashboard",
          source: SOCKET_CHANNELS.EETDAGBOEK,
        };
      }
      if (p.kind === "eetdagboek_admin_comment") {
        return {
          title: "Eetdagboek",
          message: "Uw begeleider heeft feedback toegevoegd aan uw eetdagboek.",
          actorName: p.admin ? String(p.admin) : pickActorName(p) || null,
          type: "info",
          link: "/eetdagboek",
          source: SOCKET_CHANNELS.EETDAGBOEK,
        };
      }
      return {
        title: "Eetdagboek",
        message: "Er is een update in het eetdagboek.",
        type: "info",
        link: "/eetdagboek",
        source: SOCKET_CHANNELS.EETDAGBOEK,
      };
    case SOCKET_CHANNELS.DOCUMENTEN:
      if (p._forAdmins) {
        return {
          title: "Documenten",
          message: "Een cliënt heeft een document toegevoegd of aangepast.",
          type: "info",
          link: "/dashboard",
          source: SOCKET_CHANNELS.DOCUMENTEN,
        };
      }
      if (
        p.kind === "advies_document_admin" ||
        p.kind === "document_admin_advies"
      ) {
        return {
          title: "Documenten & adviezen",
          message: "Uw begeleider heeft een advies of document voor u toegevoegd.",
          type: "info",
          link: "/documents",
          source: SOCKET_CHANNELS.DOCUMENTEN,
        };
      }
      if (p.kind === "document_admin_share") {
        return {
          title: "Documenten",
          message: "Uw begeleider heeft documenten met u gedeeld of bijgewerkt.",
          type: "info",
          link: "/documents",
          source: SOCKET_CHANNELS.DOCUMENTEN,
        };
      }
      return {
        title: "Documenten",
        message: "Er is een update bij uw documenten of adviezen.",
        type: "info",
        link: "/documents",
        source: SOCKET_CHANNELS.DOCUMENTEN,
      };
    case SOCKET_CHANNELS.VOORUITGANG:
      if (p._forAdmins) {
        return {
          title: "Vooruitgang",
          message:
            p.kind === "vooruitgang_admin_feedback"
              ? "Een cliënt heeft vooruitgang ingevuld; er staat ook begeleidersfeedback."
              : "Een cliënt heeft de vooruitgang / evolutie bijgewerkt.",
          type: "info",
          link: "/dashboard",
          source: SOCKET_CHANNELS.VOORUITGANG,
        };
      }
      if (p.kind === "vooruitgang_admin_feedback") {
        return {
          title: "Vooruitgang",
          message: "Uw begeleider heeft feedback toegevoegd bij uw vooruitgang / evolutie.",
          type: "info",
          link: "/evolution",
          source: SOCKET_CHANNELS.VOORUITGANG,
        };
      }
      return {
        title: "Vooruitgang",
        message: "Uw vooruitgang / evolutie is bijgewerkt.",
        type: "info",
        link: "/evolution",
        source: SOCKET_CHANNELS.VOORUITGANG,
      };
    case SOCKET_CHANNELS.COMMENTS:
      return {
        title: "Reacties",
        message: "Er is een nieuwe reactie of opmerking.",
        actorName: pickActorName(p) || null,
        avatarUrl: pickAvatar(p) || null,
        type: "info",
        link: "/planning",
        source: SOCKET_CHANNELS.COMMENTS,
      };
    case SOCKET_CHANNELS.VRAGENLIJSTEN:
      if (p._forAdmins) {
        const label =
          p.questionnaire === "voeding"
            ? "voedingsvragenlijst"
            : p.questionnaire
              ? String(p.questionnaire)
              : "vragenlijst";
        return {
          title: "Vragenlijsten",
          message: `Een cliënt heeft de ${label} verstuurd.`,
          type: "info",
          link: "/dashboard",
          source: SOCKET_CHANNELS.VRAGENLIJSTEN,
        };
      }
      if (p.kind === "vragenlijst_admin_feedback") {
        const q = p.questionnaire;
        let detail = "vragenlijsten";
        if (q === "hongerenquete") detail = "hongerenquête";
        else if (q === "dagelijkse_gewoonten") detail = "dagelijkse gewoonten";
        else if (q === "voeding") detail = "voedingsvragenlijst";
        else if (q) detail = String(q);
        return {
          title: "Vragenlijsten",
          message: `Uw begeleider heeft feedback toegevoegd bij uw ${detail}.`,
          type: "info",
          link: "/healthforms",
          source: SOCKET_CHANNELS.VRAGENLIJSTEN,
        };
      }
      return {
        title: "Vragenlijsten",
        message: "Er is een update op uw voedingsvragenlijst.",
        type: "info",
        link: "/healthforms",
        source: SOCKET_CHANNELS.VRAGENLIJSTEN,
      };
    case SOCKET_CHANNELS.KLACHTENFORMULIER:
      if (p._forAdmins) {
        return {
          title: "Klachtenregistratie",
          message: "Een cliënt heeft de klachtenregistratie verstuurd.",
          type: "info",
          link: "/dashboard",
          source: SOCKET_CHANNELS.KLACHTENFORMULIER,
        };
      }
      if (p.kind === "klachten_admin_feedback") {
        return {
          title: "Klachtenregistratie",
          message: "Uw begeleider heeft feedback toegevoegd aan uw klachtenregistratie.",
          type: "info",
          link: "/healthforms",
          source: SOCKET_CHANNELS.KLACHTENFORMULIER,
        };
      }
      return {
        title: "Klachtenregistratie",
        message: "Uw klachtenregistratie is verstuurd of bijgewerkt.",
        type: "info",
        link: "/healthforms",
        source: SOCKET_CHANNELS.KLACHTENFORMULIER,
      };
    case SOCKET_CHANNELS.CHAT: {
      const preview = p.preview ? String(p.preview) : "";
      return {
        title: "Chat",
        message: preview
          ? `Nieuw bericht${p.author ? ` van ${String(p.author)}` : ""}: ${preview.length > 80 ? `${preview.slice(0, 80)}…` : preview}`
          : "Er is een nieuw chatbericht.",
        actorName: p.author ? String(p.author) : null,
        type: "info",
        link: "/chatroom",
        source: SOCKET_CHANNELS.CHAT,
      };
    }
    case SOCKET_CHANNELS.SUPPORT: {
      const subj = p.subject ? String(p.subject) : "Support";
      if (p._forAdmins) {
        if (p.kind === "support_ticket_new") {
          return {
            title: "Support",
            message: `Nieuw ticket: ${subj}`,
            type: "info",
            link: "/support",
            source: SOCKET_CHANNELS.SUPPORT,
          };
        }
        if (p.kind === "support_ticket_message") {
          return {
            title: "Support",
            message: p.preview
              ? `Nieuw bericht op ticket (${subj}): ${String(p.preview).slice(0, 90)}${String(p.preview).length > 90 ? "…" : ""}`
              : `Nieuw bericht op ticket: ${subj}`,
            type: "info",
            link: "/support",
            source: SOCKET_CHANNELS.SUPPORT,
          };
        }
        if (p.kind === "support_ticket_assigned") {
          return {
            title: "Support",
            message: `Een ticket is aan u toegewezen: ${subj}`,
            type: "info",
            link: "/support",
            source: SOCKET_CHANNELS.SUPPORT,
          };
        }
        return {
          title: "Support",
          message: "Er is een update bij een supportticket.",
          type: "info",
          link: "/support",
          source: SOCKET_CHANNELS.SUPPORT,
        };
      }
      if (p.kind === "support_ticket_message") {
        return {
          title: "Support",
          message: p.preview
            ? `Uw begeleider heeft gereageerd: ${String(p.preview).slice(0, 100)}${String(p.preview).length > 100 ? "…" : ""}`
            : "Uw begeleider heeft gereageerd op uw supportticket.",
          type: "info",
          link: "/support",
          source: SOCKET_CHANNELS.SUPPORT,
        };
      }
      if (p.kind === "support_ticket_status") {
        return {
          title: "Support",
          message: `De status van uw ticket is bijgewerkt${p.status ? ` (${String(p.status)})` : ""}.`,
          type: "info",
          link: "/support",
          source: SOCKET_CHANNELS.SUPPORT,
        };
      }
      if (p.kind === "support_ticket_assigned") {
        return {
          title: "Support",
          message: "Uw ticket is toegewezen aan een begeleider.",
          type: "info",
          link: "/support",
          source: SOCKET_CHANNELS.SUPPORT,
        };
      }
      return {
        title: "Support",
        message: "Er is een update op uw supportticket.",
        type: "info",
        link: "/support",
        source: SOCKET_CHANNELS.SUPPORT,
      };
    }
    case SOCKET_CHANNELS.PAYMENTS:
      return {
        title: "Betalingen",
        message:
          p.event === "created"
            ? "Er is een nieuwe betalingsregistratie voor u toegevoegd."
            : "Uw betalingsgegevens zijn bijgewerkt.",
        type: "info",
        link: "/payment",
        source: SOCKET_CHANNELS.PAYMENTS,
      };
    default:
      return {
        title: "Melding",
        message: "U heeft een nieuwe update ontvangen.",
        type: "info",
        link: null,
        source: eventName,
      };
  }
}
